<template>
  <div>
    <!-- Core Pillars Section -->
    <section class="py-32 px-6 bg-[var(--cream)]" id="pillars">
      <div class="max-w-6xl mx-auto fade-in-content">
        <div class="text-center max-w-2xl mx-auto mb-20 space-y-4">
          <h1 class="text-5xl md:text-6xl font-bold text-[var(--forest)] italic">
            Core Pillars
          </h1>
          <p class="text-xl text-[var(--moss)] italic">
            Challenging isolation, stigma, and silence—empowering those with
            lived experience to lead conversations and shape dignified,
            inclusive healing.
          </p>
        </div>
        <div class="grid md:grid-cols-3 gap-10">
          <div
            class="bg-white p-10 blob-card shadow-lg hover:shadow-2xl transition-all border border-[var(--sand)] group relative overflow-hidden pillar-card fade-in-item"
          >
            <div class="pillar-icon-bg pillar-icon-1"></div>
            <div class="relative z-10">
              <h4 class="text-2xl font-bold text-[var(--forest)] mb-4 italic">
                Challenge Isolation
              </h4>
              <p class="text-[var(--moss)] italic leading-relaxed">
                A bridge from isolation to supportive community.
              </p>
            </div>
          </div>
          <div
            class="bg-white p-10 blob-card shadow-lg hover:shadow-2xl transition-all border border-[var(--sand)] group translate-y-8 relative overflow-hidden pillar-card fade-in-item"
          >
            <div class="pillar-icon-bg pillar-icon-2"></div>
            <div class="relative z-10">
              <h4 class="text-2xl font-bold text-[var(--forest)] mb-4 italic">
                Deconstruct Stigma
              </h4>
              <p class="text-[var(--moss)] italic leading-relaxed">
                Sharing lived experiences to rewrite healing narratives.
              </p>
            </div>
          </div>
          <div
            class="bg-white p-10 blob-card shadow-lg hover:shadow-2xl transition-all border border-[var(--sand)] group relative overflow-hidden pillar-card fade-in-item"
          >
            <div class="pillar-icon-bg pillar-icon-3"></div>
            <div class="relative z-10">
              <h4 class="text-2xl font-bold text-[var(--forest)] mb-4 italic">
                Empower Recovery
              </h4>
              <p class="text-[var(--moss)] italic leading-relaxed">
                Tools, resources, and advocacy skills for sustained wellness.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <div class="wave-container bg-[var(--cream)]">
      <svg
        class="wave-divider wave-bottom"
        preserveAspectRatio="none"
        viewBox="0 0 1440 320"
      >
        <path
          d="M0,192L48,176C96,160,192,128,288,112C384,96,480,96,576,122.7C672,149,768,203,864,213.3C960,224,1056,192,1152,165.3C1248,139,1344,117,1392,106.7L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
          fill="var(--forest)"
          fill-opacity="1"
        ></path>
      </svg>
    </div>

    <!-- Mission & Vision Section -->
    <section class="py-32 px-6 bg-[var(--forest)] text-[var(--cream)]">
      <div class="max-w-6xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
        <div class="space-y-8 fade-in-item">
          <div class="space-y-4">
            <h2
              class="text-[var(--terracotta)] font-bold uppercase tracking-[0.3em] text-sm"
            >
              Our Foundation
            </h2>
            <h3 class="text-5xl md:text-6xl font-bold italic">Mission</h3>
            <div class="w-32 h-1.5 bg-[var(--terracotta)] rounded-full"></div>
          </div>
          <p class="text-xl text-[var(--sage)] italic leading-relaxed">
            To provide a safe, inclusive, and empowering space for individuals
            with lived mental health experience through professional support,
            peer networks, and community-based care.
          </p>
        </div>
        <div class="space-y-8 fade-in-item">
          <div class="space-y-4">
            <h2
              class="text-[var(--terracotta)] font-bold uppercase tracking-[0.3em] text-sm"
            >
              Our Aspiration
            </h2>
            <h3 class="text-5xl md:text-6xl font-bold italic">Vision</h3>
            <div class="w-32 h-1.5 bg-[var(--terracotta)] rounded-full"></div>
          </div>
          <p class="text-xl text-[var(--sage)] italic leading-relaxed">
            A world where lived mental health experiences drive meaningful
            change in policy, research, and society, with mental health equity
            shaped by those who have walked the path of healing.
          </p>
        </div>
      </div>
    </section>

    <div class="wave-container bg-[var(--forest)]">
      <svg
        class="wave-divider wave-bottom"
        preserveAspectRatio="none"
        viewBox="0 0 1440 320"
      >
        <path
          d="M0,224L60,202.7C120,181,240,139,360,149.3C480,160,600,224,720,240C840,256,960,224,1080,186.7C1200,149,1320,107,1380,85.3L1440,64L1440,320L1380,320C1320,320,1200,320,1080,320C960,320,840,320,720,320C600,320,480,320,360,320C240,320,120,320,60,320L0,320Z"
          fill="var(--cream)"
          fill-opacity="1"
        ></path>
      </svg>
    </div>

    <!-- Strategic Goals Section -->
    <section
      class="py-32 px-6 bg-[var(--cream)] text-[var(--forest)]"
      id="goals"
    >
      <div class="max-w-6xl mx-auto fade-in-content">
        <div
          class="flex flex-col md:flex-row justify-between items-end gap-12 mb-20"
        >
          <div class="max-w-2xl space-y-6">
            <h2 class="text-5xl font-bold italic text-[var(--forest)]">Our Strategic Objectives</h2>
            <p class="text-xl text-[var(--moss)] italic">
              We are building more than a collective; we are building a standard
              for the future of mental healthcare.
            </p>
          </div>
          <div
            class="size-24 blob-card bg-[var(--terracotta)] opacity-50 flex items-center justify-center rotate-12"
          >
            <span class="material-symbols-outlined text-5xl">auto_awesome</span>
          </div>
        </div>
        <div class="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div
            class="p-8 border-2 border-[var(--moss)] blob-card hover:bg-[var(--moss)] transition-colors relative overflow-hidden goal-card fade-in-item"
          >
            <div class="goal-icon-bg goal-icon-1"></div>
            <div class="relative z-10 px-2">
              <h5 class="text-2xl font-bold mb-4 italic text-[var(--forest)]">Dignified Space</h5>
              <p class="text-[var(--moss)] italic leading-relaxed">
                Offer a dignified space for those with lived mental health
                experience to heal, lead, and thrive.
              </p>
            </div>
          </div>
          <div
            class="p-8 border-2 border-[var(--moss)] blob-card hover:bg-[var(--moss)] transition-colors mt-8 md:mt-0 relative overflow-hidden goal-card fade-in-item"
          >
            <div class="goal-icon-bg goal-icon-2"></div>
            <div class="relative z-10 px-2">
              <h5 class="text-2xl font-bold mb-4 italic text-[var(--forest)]">Tailored Programs</h5>
              <p class="text-[var(--moss)] italic leading-relaxed">
                Provide tailored wellness programs and behavioral support.
              </p>
            </div>
          </div>
          <div
            class="p-8 border-2 border-[var(--moss)] blob-card hover:bg-[var(--moss)] transition-colors relative overflow-hidden goal-card fade-in-item"
          >
            <div class="goal-icon-bg goal-icon-3"></div>
            <div class="relative z-10 px-2">
              <h5 class="text-2xl font-bold mb-4 italic text-[var(--forest)]">
                Amplify LMHE Voices
              </h5>
              <p class="text-[var(--moss)] italic leading-relaxed">
                Amplify LMHE voices in mental health research, policymaking, and
                intervention design.
              </p>
            </div>
          </div>
          <div
            class="p-8 border-2 border-[var(--moss)] blob-card hover:bg-[var(--moss)] transition-colors mt-8 lg:mt-0 relative overflow-hidden goal-card fade-in-item"
          >
            <div class="goal-icon-bg goal-icon-4"></div>
            <div class="relative z-10 px-2">
              <h5 class="text-2xl font-bold mb-4 italic text-[var(--forest)]">Peer-Led Care</h5>
              <p class="text-[var(--moss)] italic leading-relaxed">
                Create peer-led systems of care that honor lived realities.
              </p>
            </div>
          </div>
          <div
            class="p-8 border-2 border-[var(--moss)] blob-card hover:bg-[var(--moss)] transition-colors relative overflow-hidden goal-card fade-in-item"
          >
            <div class="goal-icon-bg goal-icon-5"></div>
            <div class="relative z-10 px-2">
              <h5 class="text-2xl font-bold mb-4 italic text-[var(--forest)]">Challenge Stigma</h5>
              <p class="text-[var(--moss)] italic leading-relaxed">
                Challenge stigma and educate communities through storytelling
                and shared knowledge.
              </p>
            </div>
          </div>
          <div
            class="p-8 border-2 border-[var(--moss)] blob-card hover:bg-[var(--moss)] transition-colors mt-8 lg:mt-0 relative overflow-hidden goal-card fade-in-item"
          >
            <div class="goal-icon-bg goal-icon-6"></div>
            <div class="relative z-10 px-2">
              <h5 class="text-2xl font-bold mb-4 italic text-[var(--forest)]">LMHE Engagement</h5>
              <p class="text-[var(--moss)] italic leading-relaxed">
                Guide individuals and organizations on respectful and impactful
                LMHE engagement.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { onMounted } from "vue";

export default {
  name: "AboutPage",
  setup() {
    // Staggered fade-in for content and items
    const setupSectionFadeIn = () => {
      // Observer for main content containers
      const contentElements = document.querySelectorAll(".fade-in-content");
      let visibleContentCount = 0;

      const contentObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (
              entry.isIntersecting &&
              !entry.target.classList.contains("fade-in-visible")
            ) {
              const delay = visibleContentCount * 150;
              setTimeout(() => {
                entry.target.classList.add("fade-in-visible");
              }, delay);
              visibleContentCount++;
              contentObserver.unobserve(entry.target);
            }
          });
        },
        {
          rootMargin: "-10% 0px -10% 0px",
          threshold: 0.1,
        },
      );

      contentElements.forEach((content) => {
        contentObserver.observe(content);
      });

      // Observer for individual items (cards, columns, etc.)
      const itemElements = document.querySelectorAll(".fade-in-item");
      let visibleItemCount = 0;

      const itemObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (
              entry.isIntersecting &&
              !entry.target.classList.contains("fade-in-visible")
            ) {
              const delay = visibleItemCount * 100; // 100ms delay between items for faster stagger
              setTimeout(() => {
                entry.target.classList.add("fade-in-visible");
              }, delay);
              visibleItemCount++;
              itemObserver.unobserve(entry.target);
            }
          });
        },
        {
          rootMargin: "-5% 0px -5% 0px", // Trigger earlier for items
          threshold: 0.1,
        },
      );

      itemElements.forEach((item) => {
        itemObserver.observe(item);
      });
    };

    onMounted(() => {
      // Setup section fade-in animations
      setupSectionFadeIn();
    });
  },
};
</script>

<style scoped>
.pillar-card {
  position: relative;
  transition: border-radius 0.5s ease-in-out;
}

.pillar-card:hover {
  border-radius: 50% 50% 45% 55% / 60% 60% 40% 40% !important;
}

.pillar-icon-bg {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 0;
  opacity: 0.5;
  transition: transform 0.5s ease, opacity 0.5s ease;
}

.pillar-card:hover .pillar-icon-bg {
  transform: translate(-50%, -50%) scale(1.1);
  opacity: 0.65;
}

.pillar-icon-bg::before {
  content: "";
  font-family: "Material Symbols Outlined";
  font-size: 80px;
  line-height: 1;
  color: var(--sand);
  font-variation-settings: "FILL" 0, "wght" 400, "GRAD" 0, "opsz" 48;
}

.pillar-icon-1::before {
  content: "diversity_3";
}

.pillar-icon-2::before {
  content: "psychology";
}

.pillar-icon-3::before {
  content: "volunteer_activism";
}

.goal-card {
  position: relative;
  transition: border-radius 0.5s ease-in-out, background-color 0.3s ease;
}

.goal-card:hover {
  border-radius: 50% 50% 45% 55% / 60% 60% 40% 40% !important;
  background-color: var(--moss) !important;
}

.goal-icon-bg {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
  z-index: 0;
  opacity: 0.15;
  transition: transform 0.5s ease, opacity 0.5s ease;
}

.goal-card:hover .goal-icon-bg {
  transform: translate(-50%, -50%) scale(1.1);
  opacity: 0.1;
}

.goal-icon-bg::before {
  content: "";
  font-family: "Material Symbols Outlined";
  font-size: 80px;
  line-height: 1;
  color: var(--terracotta);
  font-variation-settings: "FILL" 0, "wght" 400, "GRAD" 0, "opsz" 48;
}

.goal-icon-1::before {
  content: "meeting_room";
}

.goal-icon-2::before {
  content: "schema";
}

.goal-icon-3::before {
  content: "campaign";
}

.goal-icon-4::before {
  content: "groups";
}

.goal-icon-5::before {
  content: "psychology";
}

.goal-icon-6::before {
  content: "handshake";
}

.goal-card:hover .relative.z-10 h5 {
  color: white !important;
}

.goal-card:hover .relative.z-10 p {
  color: var(--sage) !important;
}

/* Staggered fade-in content */
.fade-in-content {
  opacity: 0;
  transform: translateY(30px);
  transition: opacity 0.8s ease-out, transform 0.8s ease-out;
}

.fade-in-content.fade-in-visible {
  opacity: 1;
  transform: translateY(0);
}

/* Staggered fade-in items */
.fade-in-item {
  opacity: 0;
  transform: translateY(30px);
  transition: opacity 0.8s ease-out, transform 0.8s ease-out;
}

.fade-in-item.fade-in-visible {
  opacity: 1;
  transform: translateY(0);
}
</style>
